import * as Rally from '@rally/ui-themes/dist/web/rally/tokens.es6';
import * as Uhc from '@rally/ui-themes/dist/web/uhc/tokens.es6';

import { env } from '@pass-frontend/features/configuration/util';

const { BASE_PATH, FF_RENEWACTIVE_COPY_UPDATES_2026 } = env();

export interface UiTheme {
  chromePrimaryColor: string;
  primaryColor: string;
  brandColor: string;
  tabButtonText: string;
  bodyText: string;
  colorDandelion: string;
  colorSunsetOrangeDark: string;
  colorSunsetOrange: string;
  colorSunsetOrangeLight: string;
  colorNeutral: string;
  colorNeutral80: string;
  colorNeutral50: string;
  successColor: string;
  errorTextDangerColor: string;
  errorTextDangerColorDark: string;
  colorLightNeutral: string;
  colorMediumLightNeutral: string;
  colorMediumNeutral: string;
  colorLowNeutral: string;
  colorWhite: string;
  colorCamaroneLight: string;
}

/**
 * Used by components specific to Rally/One Pass theme
 */
export interface RallyTheme extends UiTheme {
  colorCeruleanLight: string;
  colorCeruleanDark: string;
}

/**
 * Used by components specific to UHC/Arcade theme
 */
export interface UHCTheme extends UiTheme {
  blueDark: string;
  primaryColorDark: string;
}

export interface ImageTheme {
  siteLogo: string;
  siteLogoSelect?: string;
  socialOnlineEvent: string;
  onlineFitnessCodeBanner: string;
  welcomeToOnePass: string;
  codeForEverything: string;
  onlineFitnessCode: string;
  noLocation: string;
  unauthorizedIcon: string;
  onePassOnboardingModal: string;
  useYourCode?: string;
  familyWellness?: string;
  successCheckmark?: string;
  hsidLogo?: string;
  helpCenter?: string;
  systemClose?: string;
}

// Yellow: taken from Rally.RDS_COLOR_SYSTEM_DANDELION. no similar color in UHC
const colorDandelion = '#ffcb61';

export const rallyTheme: RallyTheme = {
  chromePrimaryColor: Rally.RDS_COLOR_SYSTEM_CHARCOAL_DARK,
  brandColor: Rally.RDS_COLOR_SYSTEM_RALLY_BRAND_ORANGE,
  tabButtonText: Rally.RDS_COLOR_SYSTEM_CHARCOAL_LIGHT,
  primaryColor: Rally.RDS_COLOR_SYSTEM_CERULEAN,
  bodyText: Rally.RDS_COLOR_SYSTEM_CHARCOAL,
  colorDandelion,
  colorSunsetOrangeDark: Rally.RDS_COLOR_SYSTEM_SUNSET_ORANGE_DARK,
  colorSunsetOrange: Rally.RDS_COLOR_SYSTEM_SUNSET_ORANGE,
  colorSunsetOrangeLight: Rally.RDS_COLOR_SYSTEM_SUNSET_ORANGE_LIGHT,
  colorNeutral: Rally.RDS_COLOR_NEUTRAL_90,
  colorNeutral80: Rally.RDS_COLOR_NEUTRAL_80,
  colorNeutral50: Rally.RDS_COLOR_NEUTRAL_50,
  successColor: Rally.RDS_COLOR_SYSTEM_CAMARONE,
  errorTextDangerColor: Rally.RDS_COLOR_SYSTEM_GRENADIER,
  errorTextDangerColorDark: Rally.RDS_COLOR_SYSTEM_GRENADIER_DARK,
  colorLightNeutral: Rally.RDS_COLOR_NEUTRAL_98,
  colorMediumLightNeutral: Rally.RDS_COLOR_NEUTRAL_95,
  colorMediumNeutral: Rally.RDS_COLOR_SYSTEM_DEEP_TURQUOISE_LIGHT,
  colorLowNeutral: Rally.RDS_COLOR_NEUTRAL_80,
  colorWhite: Rally.RDS_COLOR_WHITE,
  colorCeruleanLight: Rally.RDS_COLOR_SYSTEM_CERULEAN_LIGHT,
  colorCeruleanDark: Rally.RDS_COLOR_SYSTEM_CERULEAN_DARK,
  colorCamaroneLight: Rally.RDS_COLOR_SYSTEM_CAMARONE_LIGHT,
};

export const uhcTheme: UHCTheme = {
  chromePrimaryColor: Uhc.RDS_COLOR_SYSTEM_UHC_GREY_DARK,
  brandColor: Uhc.RDS_COLOR_SYSTEM_UHC_BRAND_BLUE,
  tabButtonText: Uhc.RDS_COLOR_NEUTRAL_50,
  primaryColor: Uhc.RDS_COLOR_SYSTEM_UHC_BLUE,
  primaryColorDark: Uhc.RDS_COLOR_SYSTEM_UHC_BLUE_DARK,
  bodyText: Uhc.RDS_COLOR_SYSTEM_UHC_GREY,
  colorDandelion,
  colorSunsetOrangeDark: Uhc.RDS_COLOR_SYSTEM_AMBER_ORANGE_DARK,
  colorSunsetOrangeLight: Uhc.RDS_COLOR_SYSTEM_AMBER_ORANGE_LIGHT,
  colorSunsetOrange: Rally.RDS_COLOR_SYSTEM_SUNSET_ORANGE,
  colorNeutral: Uhc.RDS_COLOR_NEUTRAL_90,
  colorNeutral80: Uhc.RDS_COLOR_NEUTRAL_80,
  colorNeutral50: Uhc.RDS_COLOR_NEUTRAL_50,
  successColor: Uhc.RDS_COLOR_SYSTEM_KELLY_GREEN,
  errorTextDangerColor: Uhc.RDS_COLOR_SYSTEM_RUBY_RED,
  errorTextDangerColorDark: Uhc.RDS_COLOR_SYSTEM_RUBY_RED_DARK,
  colorLightNeutral: Uhc.RDS_COLOR_NEUTRAL_98,
  colorMediumLightNeutral: Rally.RDS_COLOR_NEUTRAL_95,
  colorMediumNeutral: Uhc.RDS_COLOR_SYSTEM_OLYMPIC_BLUE_LIGHT,
  colorLowNeutral: Uhc.RDS_COLOR_NEUTRAL_80,
  colorWhite: Uhc.RDS_COLOR_WHITE,
  blueDark: Uhc.RDS_COLOR_SYSTEM_OLYMPIC_BLUE_DARK,
  colorCamaroneLight: Rally.RDS_COLOR_SYSTEM_CAMARONE_LIGHT,
};

export const onePassImageUrls: ImageTheme = {
  siteLogo: `${BASE_PATH}/one-pass/1p-site-logo.svg`,
  siteLogoSelect: `${BASE_PATH}/one-pass/1p-site-logo-new.svg`,
  socialOnlineEvent: `${BASE_PATH}/one-pass/1p-grouper-logo.png`,
  onlineFitnessCodeBanner: `${BASE_PATH}/one-pass/1p-online-fitness-code-banner.svg`,
  welcomeToOnePass: `${BASE_PATH}/one-pass/1p-welcome-to-one-pass.svg`,
  codeForEverything: `${BASE_PATH}/one-pass/1p-code-for-everything.svg`,
  onlineFitnessCode: `${BASE_PATH}/one-pass/1p-online-fitness-code.svg`,
  noLocation: `${BASE_PATH}/one-pass/1p-no-location.svg`,
  unauthorizedIcon: `${BASE_PATH}/one-pass/1p-lock-unauthorized-page.png`,
  onePassOnboardingModal: `${BASE_PATH}/one-pass/1p-onboarding-modal-hero.png`,
  useYourCode: `${BASE_PATH}/one-pass/1p-use-your-code.svg`,
  familyWellness: `${BASE_PATH}/one-pass/1p-family-wellness.svg`,
  successCheckmark: `${BASE_PATH}/one-pass/1p-success-checkmark.svg`,
  hsidLogo: `${BASE_PATH}/one-pass/1p-hsid.svg`,
  helpCenter: `${BASE_PATH}/one-pass/1p-help-center.svg`,
  systemClose: `${BASE_PATH}/one-pass/1p-system-close.svg`,
};

export const renewActiveImageUrls: ImageTheme = {
  siteLogo: `${BASE_PATH}/renew-active/ra-site-logo.svg`,
  socialOnlineEvent: `${BASE_PATH}/renew-active/ra-grouper-logo-bright-cherry.png`,
  onlineFitnessCodeBanner: FF_RENEWACTIVE_COPY_UPDATES_2026
    ? `${BASE_PATH}/renew-active/ra-online-fitness-code-banner-2026.png`
    : `${BASE_PATH}/renew-active/ra-online-fitness-code-banner.png`,
  welcomeToOnePass: FF_RENEWACTIVE_COPY_UPDATES_2026
    ? `${BASE_PATH}/renew-active/ra-welcome-to-renew-active-2026.jpg`
    : `${BASE_PATH}/renew-active/ra-welcome-to-one-pass.png`,
  codeForEverything: FF_RENEWACTIVE_COPY_UPDATES_2026
    ? `${BASE_PATH}/renew-active/ra-code-for-everything-2026.jpg`
    : `${BASE_PATH}/renew-active/ra-code-for-everything.png`,
  onlineFitnessCode: FF_RENEWACTIVE_COPY_UPDATES_2026
    ? `${BASE_PATH}/renew-active/ra-online-fitness-code-2026.jpg`
    : `${BASE_PATH}/renew-active/ra-online-fitness-code.png`,
  noLocation: FF_RENEWACTIVE_COPY_UPDATES_2026
    ? `${BASE_PATH}/renew-active/ra-no-location-2026.jpg`
    : `${BASE_PATH}/renew-active/ra-no-location.png`,
  unauthorizedIcon: `${BASE_PATH}/renew-active/ra-lock-unauthorized-page.svg`,
  onePassOnboardingModal: `${BASE_PATH}/renew-active/1p-onboarding-modal-hero.png`,
};
